/**
 * AntispamSettingsPage — 反垃圾设置（独立页）
 * DNSBL 黑名单区域、入站最小接收间隔、拒收域名黑名单。
 * 数据存 server_config 表，后端 SMTP 入站检查时实时读取，保存即生效，无需重启服务。
 */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Field,
  InfoLabel,
  Input,
  Textarea,
  Switch,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Divider,
} from '@fluentui/react-components'
import { SaveRegular, DeleteRegular, AddRegular } from '@fluentui/react-icons'
import { antispamApi } from '@/api/antispam'
import { blockedDomainsApi, type BlockedDomain } from '@/api/blockedDomains'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', minWidth: 0 },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    paddingTop: '16px',
  },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  list: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: 4 },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '8px 12px',
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground2,
  },
  domain: { fontWeight: 600 },
  note: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  addRow: { display: 'flex', gap: '8px', marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-end' },
})

export default function AntispamSettingsPage() {
  const styles = useStyles()
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['antispam'], queryFn: () => antispamApi.get() })

  const [dnsblEnabled, setDnsblEnabled] = useState(true)
  const [dnsblZones, setDnsblZones] = useState('')
  const [minInterval, setMinInterval] = useState(15)

  // 拒收域名黑名单
  const { data: blocked = [], isLoading: blockedLoading } = useQuery({
    queryKey: ['blocked-domains'],
    queryFn: () => blockedDomainsApi.list(),
  })
  const [newDomain, setNewDomain] = useState('')
  const [newNote, setNewNote] = useState('')

  useEffect(() => {
    if (data) {
      setDnsblEnabled(data.dnsbl_enabled)
      setDnsblZones((data.dnsbl_zones ?? []).join('\n'))
      setMinInterval(data.min_receive_interval_secs ?? 15)
    }
  }, [data])

  const saveMutation = useMutation({
    mutationFn: () =>
      antispamApi.put({
        dnsbl_enabled: dnsblEnabled,
        dnsbl_zones: dnsblZones.split('\n').map((s) => s.trim()).filter(Boolean),
        min_receive_interval_secs: minInterval,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['antispam'] })
      toast.success('已保存', '反垃圾设置已更新，入站邮件实时生效')
    },
    onError: (err) => toast.error('保存失败', err instanceof ApiError ? err.message : '请检查输入'),
  })

  const addMutation = useMutation({
    mutationFn: () => blockedDomainsApi.add(newDomain, newNote || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-domains'] })
      setNewDomain('')
      setNewNote('')
      toast.success('已添加', '该发件域的入站邮件将被直接拒收')
    },
    onError: (err) => toast.error('添加失败', err instanceof ApiError ? err.message : '请检查域名'),
  })

  const deleteMutation = useMutation({
    mutationFn: (domain: string) => blockedDomainsApi.remove(domain),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-domains'] })
      toast.success('已移除', '该域名已不再被拒收')
    },
    onError: (err) => toast.error('移除失败', err instanceof ApiError ? err.message : '请重试'),
  })

  const dirty =
    !!data &&
    (data.dnsbl_enabled !== dnsblEnabled ||
      (data.dnsbl_zones ?? []).join('\n') !== dnsblZones.trim() ||
      (data.min_receive_interval_secs ?? 15) !== minInterval)

  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <Spinner size="large" label="加载中..." />
      </div>
    )
  }

  const reset = () => {
    if (!data) return
    setDnsblEnabled(data.dnsbl_enabled)
    setDnsblZones((data.dnsbl_zones ?? []).join('\n'))
    setMinInterval(data.min_receive_interval_secs ?? 15)
  }

  return (
    <div className={styles.root}>
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>实时生效</MessageBarTitle>
          以下设置保存在服务器配置中，SMTP 入站检查时实时读取，保存后立即对新连接生效，无需重启服务。
        </MessageBarBody>
      </MessageBar>

      <Card size="small">
        <Text style={{ fontWeight: 600 }}>拒收域名黑名单</Text>
        <Text className={styles.hint}>
          配置不接收的发件域名（按发件域精确匹配，自动转小写）。命中后该域邮件将被直接硬拒（550 退信）。
        </Text>
        <div className={styles.list}>
          {blockedLoading ? (
            <Spinner size="small" label="加载黑名单…" />
          ) : blocked.length === 0 ? (
            <Text className={styles.note}>暂无黑名单域名</Text>
          ) : (
            (blocked as BlockedDomain[]).map((b) => (
              <div key={b.domain} className={styles.row}>
                <div style={{ minWidth: 0 }}>
                  <div className={styles.domain}>{b.domain}</div>
                  {b.note && <div className={styles.note}>{b.note}</div>}
                </div>
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => deleteMutation.mutate(b.domain)}
                  disabled={deleteMutation.isPending}
                >
                  移除
                </Button>
              </div>
            ))
          )}
        </div>
        <div className={styles.addRow}>
          <Field label="域名" style={{ flex: '1 1 220px', minWidth: 180 }}>
            <Input
              value={newDomain}
              onChange={(_, d) => setNewDomain(d.value)}
              placeholder="example.com"
            />
          </Field>
          <Field label="备注（可选）" style={{ flex: '1 1 220px', minWidth: 180 }}>
            <Input value={newNote} onChange={(_, d) => setNewNote(d.value)} placeholder="如：垃圾来源" />
          </Field>
          <Button
            appearance="primary"
            icon={<AddRegular />}
            onClick={() => addMutation.mutate()}
            disabled={!newDomain.trim() || addMutation.isPending}
          >
            添加
          </Button>
        </div>
      </Card>

      <Card size="small">
        <Text style={{ fontWeight: 600 }}>入站最小接收间隔</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: 12 }}>
          <Field
            label={
              <InfoLabel info="同一「来源 IP + 发件人 + 收件人」组合在间隔内只允许成功接收一封邮件，其余直接拒收。用于防止同一来源高频投递进行爆破或刷爆收件箱。">
                最小接收间隔（秒）
              </InfoLabel>
            }
            hint="默认 15 秒；设为 0 表示不限速"
          >
            <Input
              type="number"
              value={String(minInterval)}
              onChange={(_, d) => setMinInterval(Number(d.value))}
              style={{ maxWidth: '200px' }}
            />
          </Field>
          <Text className={styles.hint}>
            例如设为 15：同一来源在 15 秒内连发多封，仅第一封入箱，其余被临时拒收（4xx）。
          </Text>
        </div>
      </Card>

      <Divider />

      <Card size="small">
        <Text style={{ fontWeight: 600 }}>DNSBL 黑名单</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: 12 }}>
          <Field
            label={
              <InfoLabel info="连接阶段查询 DNS 黑名单，命中则直接拒收。">启用 DNSBL</InfoLabel>
            }
          >
            <Switch
              checked={dnsblEnabled}
              onChange={(_, d) => setDnsblEnabled(d.checked)}
              label={dnsblEnabled ? '已启用' : '已关闭'}
            />
          </Field>
          <Field
            label={
              <InfoLabel info="每行一个黑名单区域。注意：勿添加 zen.spamhaus.org——其 PBL 会把腾讯云/阿里云等数据中心 IP 整体误杀，导致入站被硬拒。">
                黑名单区域（Zones）
              </InfoLabel>
            }
            hint="每行一个，如 bl.spamcop.net"
          >
            <Textarea
              value={dnsblZones}
              onChange={(_, d) => setDnsblZones(d.value)}
              placeholder={'bl.spamcop.net'}
              disabled={!dnsblEnabled}
              style={{ minHeight: 96 }}
            />
          </Field>
        </div>
      </Card>

      <div className={styles.actions}>
        {dirty && (
          <Button appearance="subtle" onClick={reset}>
            撤销
          </Button>
        )}
        <Button
          appearance="primary"
          icon={<SaveRegular />}
          onClick={() => saveMutation.mutate()}
          disabled={!dirty || saveMutation.isPending}
        >
          {saveMutation.isPending ? '保存中…' : '保存'}
        </Button>
      </div>
    </div>
  )
}
